/** Специальные функции для обработки ответов virtuoso */

/** Возвращает значение языка (ключ - 'xml:lang') для JSON object. Входным параметром является
 * полученная SPARQL запросом одна запись (см. bindings в ответе). Пример записи:
 * { "IRI": { "type": "uri", "value": "http://hbz.ru/schema/DegreeOfDetail" }	, "Label": { "type": "literal", "xml:lang": "en", "value": "Degree of detail" }},
 * 
 * Для примера выше значение langVar должно быть "Label"
*/
export const getLangValue = (record, langVar='object') => {
  return (record[langVar] && record[langVar].hasOwnProperty('xml:lang'))? record[langVar]['xml:lang']: undefined;
}
/** Возвращает триплеты, у которых uri соответсвует значению первого из первого элемента массива в values */
export const subsetByURI = (values, uri, field='predicat') => {
  //console.log("subsetByURI==", uri, values, field);
  const subset = values.filter(item => (item[field])&&(item[field].type=="uri")&&(item[field].value==uri));
  return Array.isArray(subset)?subset:[];
}

/** Получение подмножества (записи полученны в ответ на SPARQL запрос), у которых xml:lang соответствует параметру.
 * Параметры:
  * values - записи полученны в ответ на SPARQL запрос
  * lang - значение для сравнения с полем xml:lang 
  * langVar - наименование свойства, содержащее xml:lang
*/
export const subsetByLangOnly = (values, lang=undefined, langVar="object") =>{
  //Выбирается либо есть язык, либо без языка (или с пустым значением языка)
  const subset = (lang)? 
      values.filter(item => (item[langVar] && item[langVar].hasOwnProperty('xml:lang'))&&(item[langVar]['xml:lang']==lang))
    :
      values.filter(item => (item[langVar] && (!item[langVar].hasOwnProperty('xml:lang')||(item[langVar]['xml:lang']==""))))
    ;
  return Array.isArray(subset)?subset:[];
}
/** Отличие от предыдушего, в том, что берёт с указанным языком, если нет, то с пустым, если нет, то исходный в списке */
export const subsetByLang = (values, lang=undefined, langVar="object") =>{
  let subset = subsetByLangOnly(values, lang, langVar);
  if (subset.length>0) return subset;
  if (lang) subset = subsetByLangOnly(values, undefined, langVar);
  if (subset.length>0) return subset;
  return values;
}
/** Получение подмножества из списка записей (записи получены в ответ на SPARQL запрос) 
  * в соответствии со значением языка в xml:lang или отсутствие языка . Параметры:
  * values - записи полученны в ответ на SPARQL запрос
  * lang - значение для сравнения с полем xml:lang
  * langVar - наименование свойства, содержащее xml:lang
  * fieldVar - наименование свойства, содержащий uri предиката или IRI субъекта
  */
 export const subsetByLangOrEmpty = (values, lang, fieldVar="predicat", langVar="object") => {
  //Список всех доступных IRI/URI 
  const fields = [...new Set(values.map(item => item[fieldVar].value))];
  //Определим для каких IRI/URI есть lang
  const langs = [... new Set(subsetByLangOnly(values, lang, langVar).map(item => item[fieldVar].value))];
  //Выделяем подмножество, если есть в langs, то с языком, иначе без языка
  const subset = values.filter(item => {
    if (langs.includes(item[fieldVar].value))
      return (item[langVar].hasOwnProperty('xml:lang'))&&(item[langVar]['xml:lang']==lang);
    return (!item[langVar].hasOwnProperty('xml:lang'))||(item[langVar]['xml:lang']=="");
  })
  return subset;
}
/** Возвращает массив элементов для отображения. Используется значение для объекта */
export const makeValuesArray = (values, field="object") => {
  //console.log("makeValuesArray", values, field);
  return values.map(item=> item[field].value);
}

/** Формирует строку объектов из массива predicat->object с учётом выбранного языка.
 * Возвращает или строку или для нескольких объектов JSON.stringify()
 * uri - предикат
 * values - массив значений
 * predicatVar - наименование свойства, содержащий значение uri предиката
 * objectVar - наименование свойства, содержащий значение
 */
export const getValues = (uri, lang, values, predicatVar='predicat', objectVar="object") =>{
  const data = getValuesAsIs(uri, lang, values, predicatVar, objectVar);
  return (Array.isArray(data)&&(data.length!=1))?JSON.stringify(data): data[0];
}

/** 
 * Возвращает или значение или defaultValue из массива predicat->object с учётом выбранного языка.
 * Если в values массав -> возвращает первое значение, 
 * если масив пустой -> возвращает defaultValue
 * uri - предикат
 * values - массив значений
 * predicatVar - наименование свойства, содержащий значение uri предиката
 * objectVar - наименование свойства, содержащий значение
 */
 export const getValueOrDefault = (uri, lang, values, defaultValue, predicatVar='predicat', objectVar="object") =>{
  const data = getValuesAsIs(uri, lang, values, predicatVar, objectVar);
  if (Array.isArray(data)){
    return (data.length>0)?data[0]: defaultValue;
  };
  return (data == undefined || data == null)?defaultValue : data;
}

/** Аналогично getValues, только возвращает массив.  */
export const getValuesAsIs = (uri, lang, values, predicatVar='predicat', objectVar="object") =>{
  //Список предикатов, соответствующих uri
  const subset = subsetByURI(values, uri, predicatVar);
  //Список значений, которые соответствуют выбранному языку
  let langs = subsetByLangOnly(subset, lang, objectVar);
  //Если для выбранного языка не найдено, то попробуем получить для без назначенного языка
  if (langs.length==0)
    langs = subsetByLangOnly(subset, undefined, objectVar);
  return (langs.length>0)?makeValuesArray(langs, objectVar):makeValuesArray (subset, objectVar);
}

/** Определяет числовое значение count (может быть в полученных данных на SPARQL-запрос).
 *  Переменной нет возвращает undefined. Если это не числовое значение, то возвращается null, иначе число
*/
export const getCountValue = (values, countVar="count") => {
  const subset = (values)
    ? values.filter(item => (item[countVar])&&(item[countVar].datatype=="http://www.w3.org/2001/XMLSchema#integer"))
    : [];
  const number = (Array.isArray(subset) && subset.length>0)
    ? parseInt(subset[0][countVar].value || 0, 10)
    : undefined;
  return isNaN(number)? null : number;
}
/** Вариант, аналогичный getCountValue, только указывается значение по умолчанию, если нет данныз  */
export const getCountValue2 = (values, defaultValue, countVar="count") => {
  const count = getCountValue(values, countVar);
  return (count)? count: defaultValue;
}

/** Метод, который обновляет находит и добавляет в триплет дополнительные значения (header, values, state) по значение объекта из триплета
 * 1. data - массив данных , которые добавляются в триплет обычно это {values, head, state}
 * 2. values - Исходный массив данных 
 * 3. objectValue - значение, которое должно быть найдено для включение массива данных
 * 4. lang - язык для которому должен соответствовать  значение
 * 5. objectVar имя ключа из values по которому определяется параметр
*/
export const updateTripletsByObjectValue = (data, values, objectValue, lang=undefined, objectVar="object") => {
  for (let i = 0; i < values.length; i++) {
    const item = values[i];
    if (item[objectVar].value==objectValue){
      if (lang && (!item[objectVar].hasOwnProperty('xml:lang') || (item[objectVar]['xml:lang']!=lang))) continue;
      for(var key in data) {
        item[key] = data[key];
      }
    }
  }
  return values;
}

/** Метод из ответа SPARQL запроса возвращает список триплетов, у которых субъект имеет заданое значение  
 *  response - результат ответа SPARQL. Содержит объекты head и results
*/
export const subsetTripletsByObject = (uri, response, subjectVar="s") => {
  if (response && response.results && Array.isArray(response.results.bindings)){
    const triplets = response.results.bindings.filter(item => item[subjectVar].value == uri);
    return triplets;
  }
  return [];
}